import { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft,
  UserPlus,
  Calendar,
  FileText,
  CheckCircle,
} from "lucide-react";
import { Timestamp, writeBatch, doc } from "firebase/firestore";
import { db } from "../../lib/firebase";
import { format, differenceInDays } from "date-fns";
import clsx from "clsx";
import { where } from "firebase/firestore";
import { Card } from "../../components/ui/Card";
import { Button } from "../../components/ui/Button";
import { Input } from "../../components/ui/Input";
import { EmptyState } from "../../components/shared/EmptyState";
import { LoadingSpinner } from "../../components/shared/LoadingSpinner";
import { UpgradeGateModal } from "../../components/shared/UpgradeGateModal";
import { useAuth } from "../../hooks/useAuth";
import { usePlanGate } from "../../hooks/usePlanGate";
import { useCompanyStore } from "../../store/companyStore";
import { queryDocuments } from "../../lib/firestore";
import type {
  OffboardTemplate,
  ApprovalStep,
  ApprovalStatus,
  FlowStatus,
} from "../../types/offboarding.types";
import type { ExitInterviewTemplate } from "../../types/interview.types";
import type { AppUser } from "../../types/user.types";

const DEPARTMENTS = [
  "Engineering",
  "Product",
  "Design",
  "Sales",
  "Marketing",
  "Finance",
  "HR",
  "Operations",
  "Legal",
  "Other",
];

const EXIT_TYPES = [
  "Voluntary Resignation",
  "Involuntary",
  "Retirement",
  "Contract End",
  "Mutual Agreement",
];

interface FormErrors {
  employeeName?: string;
  employeeEmail?: string;
  employeeRole?: string;
  department?: string;
  managerName?: string;
  lastWorkingDay?: string;
  templateId?: string;
  exitType?: string;
}

export default function NewOffboarding() {
  const navigate = useNavigate();
  const { companyId, appUser } = useAuth();
  const { canStartOffboarding } = usePlanGate();
  const company = useCompanyStore((s) => s.company);

  const [templates, setTemplates] = useState<OffboardTemplate[]>([]);
  const [interviewTemplates, setInterviewTemplates] = useState<
    ExitInterviewTemplate[]
  >([]);
  const [companyUsers, setCompanyUsers] = useState<AppUser[]>([]);
  const [approverIds, setApproverIds] = useState<string[]>([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState<FormErrors>({});
  const [showUpgradeModal, setShowUpgradeModal] = useState(false);

  // Form state
  const [employeeName, setEmployeeName] = useState("");
  const [employeeEmail, setEmployeeEmail] = useState("");
  const [employeeRole, setEmployeeRole] = useState("");
  const [department, setDepartment] = useState("");
  const [managerName, setManagerName] = useState("");
  const [lastWorkingDay, setLastWorkingDay] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] = useState("");
  const [selectedInterviewTemplateId, setSelectedInterviewTemplateId] =
    useState("");
  const [exitType, setExitType] = useState("");

  useEffect(() => {
    if (!companyId) return;
    const loadTemplates = async () => {
      try {
        const results = await queryDocuments<OffboardTemplate>(
          "offboardTemplates",
          [where("companyId", "==", companyId)]
        );
        setTemplates(results);
      } catch {
        // No templates yet
      } finally {
        setLoadingTemplates(false);
      }
    };
    loadTemplates();

    const loadInterviewTemplates = async () => {
      try {
        const results = await queryDocuments<ExitInterviewTemplate>(
          "exitInterviewTemplates",
          [where("companyId", "==", companyId)],
        );
        setInterviewTemplates(results);
        const def = results.find((t) => t.isDefault) ?? results[0];
        if (def) setSelectedInterviewTemplateId(def.id);
      } catch {
        // ignore
      }
    };
    loadInterviewTemplates();

    // Company members are pickable as approvers. Filter out the current
    // user so HR can't approve their own request and inactive accounts.
    const loadUsers = async () => {
      try {
        const users = await queryDocuments<AppUser>("users", [
          where("companyId", "==", companyId),
        ]);
        setCompanyUsers(
          users.filter(
            (u) => u.isActive !== false && u.id !== appUser?.id
          )
        );
      } catch {
        // No users yet — approvers section will be empty
      }
    };
    loadUsers();
  }, [companyId, appUser?.id]);

  const selectedTemplate = templates.find((t) => t.id === selectedTemplateId);

  const daysRemaining =
    lastWorkingDay
      ? differenceInDays(new Date(lastWorkingDay), new Date())
      : null;

  function validate(): boolean {
    const newErrors: FormErrors = {};
    if (!employeeName.trim()) newErrors.employeeName = "Employee name is required";
    if (!employeeEmail.trim()) {
      newErrors.employeeEmail = "Email is required";
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(employeeEmail)) {
      newErrors.employeeEmail = "Invalid email address";
    }
    if (!employeeRole.trim()) newErrors.employeeRole = "Job title is required";
    if (!department) newErrors.department = "Department is required";
    if (!managerName.trim()) newErrors.managerName = "Manager name is required";
    if (!lastWorkingDay) {
      newErrors.lastWorkingDay = "Last working day is required";
    } else if (new Date(lastWorkingDay) <= new Date()) {
      newErrors.lastWorkingDay = "Must be a future date";
    }
    if (!selectedTemplateId) newErrors.templateId = "Please select a template";
    if (!exitType) newErrors.exitType = "Exit type is required";
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  }

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    if (!validate() || !companyId || !appUser) return;

    const gate = canStartOffboarding();
    if (!gate.allowed) {
      setShowUpgradeModal(true);
      return;
    }

    setSubmitting(true);
    try {
      const flowId = crypto.randomUUID();
      const portalToken = crypto.randomUUID();
      const lwdDate = new Date(lastWorkingDay);
      // Portal access expires 7 days after last working day.
      const portalExpiresAt = Timestamp.fromDate(
        new Date(lwdDate.getTime() + 7 * 86400000)
      );

      const selectedApprovers = approverIds
        .map((id) => companyUsers.find((u) => u.id === id))
        .filter((u): u is AppUser => !!u);
      const hasApprovers = selectedApprovers.length > 0;
      const approvalSteps: ApprovalStep[] = selectedApprovers.map((u, i) => ({
        approverId: u.id,
        approverName: u.displayName || u.email,
        approverEmail: u.email,
        status: i === 0 ? "pending" : "waiting",
        decidedAt: null,
        note: "",
      }));
      const initialStatus: FlowStatus = hasApprovers
        ? "pending_approval"
        : "not_started";
      const initialApprovalStatus: ApprovalStatus = hasApprovers
        ? "pending"
        : "not_required";

      const batch = writeBatch(db);

      batch.set(doc(db, "offboardFlows", flowId), {
        id: flowId,
        companyId,
        employeeId: flowId,
        employeeName: employeeName.trim(),
        employeeEmail: employeeEmail.trim(),
        employeeRole: employeeRole.trim(),
        employeeDepartment: department,
        managerId: appUser.id,
        templateId: selectedTemplateId,
        interviewTemplateId: selectedInterviewTemplateId || null,
        status: initialStatus,
        approvalStatus: initialApprovalStatus,
        approvalSteps,
        currentApproverIndex: hasApprovers ? 0 : -1,
        startDate: Timestamp.now(),
        lastWorkingDay: Timestamp.fromDate(lwdDate),
        completedAt: null,
        progressPercent: 0,
        portalToken,
        portalExpiresAt,
        completionScores: {
          tasks: 0,
          knowledge: 0,
          accessRevocation: 0,
          exitInterview: 0,
          assets: 0,
        },
        createdAt: Timestamp.now(),
      });

      if (selectedTemplate) {
        // Pre-allocate IDs so we can rewrite dependsOnTaskId from template
        // task IDs to the new flow task IDs.
        const idMap = new Map<string, string>();
        for (const task of selectedTemplate.tasks) {
          idMap.set(task.id, crypto.randomUUID());
        }
        for (const task of selectedTemplate.tasks) {
          const taskId = idMap.get(task.id)!;
          const dueDate = new Date(
            lwdDate.getTime() + task.dayOffset * 86400000
          );
          const dependsOn =
            task.dependsOnTaskId && idMap.has(task.dependsOnTaskId)
              ? idMap.get(task.dependsOnTaskId)!
              : null;
          batch.set(doc(db, "flowTasks", taskId), {
            id: taskId,
            // Tenant stamp — firestore.rules authorizes flowTasks through
            // resource.data.companyId, so omitting it rejects the batch.
            companyId,
            flowId,
            // Denormalized so the exit portal can resolve a task's flow
            // without an extra read.
            portalToken,
            title: task.title,
            description: task.description,
            type: task.type,
            assigneeId: "",
            assigneeRole: task.assigneeRole,
            assigneeName: "",
            dueDate: Timestamp.fromDate(dueDate),
            status: "pending",
            completedAt: null,
            completedBy: "",
            uploadedFileUrl: "",
            notes: "",
            isRequired: task.isRequired,
            dependsOnTaskId: dependsOn,
            linkUrl: task.linkUrl ?? "",
          });
        }
      }

      // usageCount is maintained by the onFlowCreatedUpdateUsage trigger —
      // firestore.rules bars clients from writing billing state, so including
      // it here would have rejected this whole batch.
      await batch.commit();
      navigate(`/offboardings/${flowId}`);
    } catch (err) {
      const message =
        err instanceof Error
          ? `Failed to create offboarding: ${err.message}`
          : "Failed to create offboarding. Please try again.";
      console.error("NewOffboarding submit failed", err);
      setErrors({ employeeName: message });
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center gap-4">
        <Link
          to="/offboardings"
          className="p-2 rounded-md text-mist hover:text-navy hover:bg-navy/5 transition-colors"
        >
          <ArrowLeft size={20} />
        </Link>
        <h1 className="text-2xl font-display text-navy">
          Start New Offboarding
        </h1>
      </div>

      <form onSubmit={handleSubmit}>
        <div className="grid lg:grid-cols-3 gap-6">
          {/* Form fields — left 2/3 */}
          <div className="lg:col-span-2 space-y-6">
            {/* Employee Information */}
            <Card>
              <h2 className="text-base font-semibold text-navy mb-4">
                Employee Information
              </h2>
              <div className="space-y-4">
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    label="Employee Full Name"
                    placeholder="e.g., Jane Smith"
                    value={employeeName}
                    onChange={(e) => setEmployeeName(e.target.value)}
                    error={errors.employeeName}
                  />
                  <Input
                    label="Employee Email"
                    type="email"
                    placeholder="jane@company.com"
                    value={employeeEmail}
                    onChange={(e) => setEmployeeEmail(e.target.value)}
                    error={errors.employeeEmail}
                  />
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    label="Job Title / Role"
                    placeholder="e.g., Senior Engineer"
                    value={employeeRole}
                    onChange={(e) => setEmployeeRole(e.target.value)}
                    error={errors.employeeRole}
                  />
                  <div className="space-y-1">
                    <label className="block text-sm font-medium text-navy">
                      Department
                    </label>
                    <select
                      value={department}
                      onChange={(e) => setDepartment(e.target.value)}
                      className={clsx(
                        "block w-full rounded-md border px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal",
                        errors.department
                          ? "border-ember focus:ring-ember/50"
                          : "border-navy/20"
                      )}
                    >
                      <option value="">Select department</option>
                      {DEPARTMENTS.map((d) => (
                        <option key={d} value={d}>
                          {d}
                        </option>
                      ))}
                    </select>
                    {errors.department && (
                      <p className="text-xs text-ember">{errors.department}</p>
                    )}
                  </div>
                </div>
                <div className="grid sm:grid-cols-2 gap-4">
                  <Input
                    label="Manager Name"
                    placeholder="e.g., John Doe"
                    value={managerName}
                    onChange={(e) => setManagerName(e.target.value)}
                    error={errors.managerName}
                  />
                  <Input
                    label="Last Working Day"
                    type="date"
                    value={lastWorkingDay}
                    onChange={(e) => setLastWorkingDay(e.target.value)}
                    error={errors.lastWorkingDay}
                  />
                </div>
                <div className="space-y-1">
                  <label className="block text-sm font-medium text-navy">
                    Exit Type
                  </label>
                  <select
                    value={exitType}
                    onChange={(e) => setExitType(e.target.value)}
                    className={clsx(
                      "block w-full rounded-md border px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal sm:max-w-xs",
                      errors.exitType
                        ? "border-ember focus:ring-ember/50"
                        : "border-navy/20"
                    )}
                  >
                    <option value="">Select exit type</option>
                    {EXIT_TYPES.map((t) => (
                      <option key={t} value={t}>
                        {t}
                      </option>
                    ))}
                  </select>
                  {errors.exitType && (
                    <p className="text-xs text-ember">{errors.exitType}</p>
                  )}
                </div>
              </div>
            </Card>

            {/* Template Selection */}
            <Card>
              <h2 className="text-base font-semibold text-navy mb-4">
                Select Template
              </h2>
              {errors.templateId && (
                <p className="text-xs text-ember mb-3">{errors.templateId}</p>
              )}
              {loadingTemplates ? (
                <div className="py-8 flex justify-center">
                  <LoadingSpinner size="md" />
                </div>
              ) : templates.length === 0 ? (
                <EmptyState
                  icon={<FileText size={48} strokeWidth={1.5} />}
                  title="No templates found"
                  description="Create an offboarding template first to define the task checklist."
                  action={
                    <Link to="/templates">
                      <Button variant="outline">
                        <FileText size={16} className="mr-1.5" />
                        Go to Templates
                      </Button>
                    </Link>
                  }
                />
              ) : (
                <div className="grid sm:grid-cols-2 gap-3">
                  {templates.map((template) => (
                    <button
                      key={template.id}
                      type="button"
                      onClick={() => setSelectedTemplateId(template.id)}
                      className={clsx(
                        "text-left rounded-lg border-2 p-4 transition-colors",
                        selectedTemplateId === template.id
                          ? "border-teal bg-teal/5"
                          : "border-navy/10 hover:border-navy/20"
                      )}
                    >
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-navy truncate">
                            {template.name}
                          </p>
                          <p className="text-xs text-mist mt-1 line-clamp-2">
                            {template.description || "No description"}
                          </p>
                        </div>
                        {selectedTemplateId === template.id && (
                          <CheckCircle
                            size={20}
                            className="text-teal flex-shrink-0"
                          />
                        )}
                      </div>
                      <p className="text-xs text-mist mt-2">
                        {template.tasks.length} tasks
                      </p>
                    </button>
                  ))}
                </div>
              )}
            </Card>

            {/* Exit interview template */}
            <Card>
              <h2 className="text-base font-semibold text-navy mb-1">
                Exit Interview Template
              </h2>
              <p className="text-xs text-mist mb-4">
                Choose the interview the employee will fill out in their portal.
                Different offboardings can use different interviews.
              </p>
              {interviewTemplates.length === 0 ? (
                <p className="text-sm text-mist">
                  No interview templates yet — the portal will fall back to your
                  default once you create one in the Interviews page.
                </p>
              ) : (
                <select
                  value={selectedInterviewTemplateId}
                  onChange={(e) =>
                    setSelectedInterviewTemplateId(e.target.value)
                  }
                  className="block w-full rounded-md border border-navy/20 px-3 py-2 text-sm text-navy focus:outline-none focus:ring-2 focus:ring-teal/50 focus:border-teal"
                >
                  <option value="">Use company default</option>
                  {interviewTemplates.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                      {t.isDefault ? " (default)" : ""}
                    </option>
                  ))}
                </select>
              )}
            </Card>

            {/* Approvers (optional, sequential) */}
            <Card>
              <h2 className="text-base font-semibold text-navy mb-1">
                Approvers <span className="text-mist font-normal text-sm">(optional)</span>
              </h2>
              <p className="text-xs text-mist mb-4">
                If selected, the employee portal email won't go out until each
                approver has signed off in order.
              </p>
              {companyUsers.length === 0 ? (
                <p className="text-sm text-mist">
                  No other company members are available to approve.
                </p>
              ) : (
                <div className="space-y-2">
                  {companyUsers.map((u) => {
                    const checked = approverIds.includes(u.id);
                    const order = approverIds.indexOf(u.id);
                    return (
                      <label
                        key={u.id}
                        className={clsx(
                          "flex items-center gap-3 rounded-md border px-3 py-2 cursor-pointer transition-colors",
                          checked
                            ? "border-teal bg-teal/5"
                            : "border-navy/10 hover:border-navy/20"
                        )}
                      >
                        <input
                          type="checkbox"
                          checked={checked}
                          onChange={(e) => {
                            setApproverIds((prev) =>
                              e.target.checked
                                ? [...prev, u.id]
                                : prev.filter((id) => id !== u.id)
                            );
                          }}
                          className="rounded border-navy/20 text-teal focus:ring-teal"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-navy truncate">
                            {u.displayName || u.email}
                          </p>
                          <p className="text-xs text-mist truncate">
                            {u.email} · {u.role.replace("_", " ")}
                          </p>
                        </div>
                        {checked && (
                          <span className="text-xs font-medium text-teal">
                            Step {order + 1}
                          </span>
                        )}
                      </label>
                    );
                  })}
                </div>
              )}
            </Card>

            {/* Submit — mobile */}
            <div className="lg:hidden">
              <Button
                type="submit"
                fullWidth
                loading={submitting}
                disabled={submitting}
              >
                <UserPlus size={16} className="mr-1.5" />
                Start Offboarding
              </Button>
            </div>
          </div>

          {/* Summary card — right 1/3 */}
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-6">
              <Card>
                <h3 className="text-sm font-semibold text-navy mb-4">
                  Summary
                </h3>
                <div className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="h-10 w-10 rounded-full bg-teal/10 flex items-center justify-center text-teal font-medium text-sm flex-shrink-0">
                      {employeeName ? employeeName.charAt(0).toUpperCase() : "?"}
                    </div>
                    <div className="min-w-0">
                      <p className="text-sm font-medium text-navy truncate">
                        {employeeName || "Employee Name"}
                      </p>
                      <p className="text-xs text-mist truncate">
                        {employeeRole || "Job Title"} {department ? `· ${department}` : ""}
                      </p>
                    </div>
                  </div>

                  <div className="border-t border-navy/5 pt-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <Calendar size={14} className="text-mist flex-shrink-0" />
                      <span className="text-mist">Last Working Day:</span>
                      <span className="text-navy font-medium">
                        {lastWorkingDay
                          ? format(new Date(lastWorkingDay), "MMM d, yyyy")
                          : "—"}
                      </span>
                    </div>
                    {daysRemaining !== null && daysRemaining > 0 && (
                      <div
                        className={clsx(
                          "inline-flex items-center px-2.5 py-1 rounded-md text-xs font-medium",
                          daysRemaining <= 7
                            ? "bg-amber-100 text-amber-700"
                            : "bg-teal/10 text-teal"
                        )}
                      >
                        {daysRemaining} days remaining
                      </div>
                    )}
                  </div>

                  <div className="border-t border-navy/5 pt-4 space-y-3">
                    <div className="flex items-center gap-2 text-sm">
                      <FileText size={14} className="text-mist flex-shrink-0" />
                      <span className="text-mist">Template:</span>
                      <span className="text-navy font-medium truncate">
                        {selectedTemplate?.name || "—"}
                      </span>
                    </div>
                    {selectedTemplate && (
                      <p className="text-xs text-mist">
                        {selectedTemplate.tasks.length} tasks will be created
                      </p>
                    )}
                  </div>

                  {exitType && (
                    <div className="border-t border-navy/5 pt-4">
                      <p className="text-xs text-mist">Exit Type</p>
                      <p className="text-sm text-navy font-medium">{exitType}</p>
                    </div>
                  )}
                </div>

                {/* Submit — desktop */}
                <div className="hidden lg:block mt-6">
                  <Button
                    type="submit"
                    fullWidth
                    loading={submitting}
                    disabled={submitting}
                  >
                    <UserPlus size={16} className="mr-1.5" />
                    Start Offboarding
                  </Button>
                </div>
              </Card>
            </div>
          </div>
        </div>
      </form>

      <UpgradeGateModal
        isOpen={showUpgradeModal}
        onClose={() => setShowUpgradeModal(false)}
        usedCount={company?.usageCount?.offboardingsThisYear ?? 0}
        limit={3}
      />
    </div>
  );
}
